import { captureException } from '@sentry/react';
import {
  type CSSProperties,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { useNavigate, useRouteError } from 'react-router-dom';

import { useTheme } from '@doger/ui/theme';

const COUNTDOWN_SECONDS = 5;

export const PagesErrorBoundary = () => {
  const error = useRouteError();
  const navigate = useNavigate();
  const secondsLeft = useCountdown(COUNTDOWN_SECONDS);
  const { theme } = useTheme();

  useLayoutEffect(() => {
    console.error(error);
    captureException(error);

    setTimeout(() => {
      navigate('/');
      window.location.reload();
    }, COUNTDOWN_SECONDS * 1_000);
  }, [error, navigate]);

  const styles = useMemo(() => {
    const isDark = theme === 'dark';

    return {
      container: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        padding: '2rem',
        backgroundColor: isDark ? '#0f0f0f' : '#f8f9fa',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      } satisfies CSSProperties,
      card: {
        maxWidth: '600px',
        width: '100%',
        padding: '2.5rem',
        backgroundColor: isDark ? '#1a1a1a' : '#ffffff',
        borderRadius: '12px',
        boxShadow: isDark
          ? '0 4px 6px rgba(0, 0, 0, 0.3), 0 2px 4px rgba(0, 0, 0, 0.2)'
          : '0 4px 6px rgba(0, 0, 0, 0.1), 0 2px 4px rgba(0, 0, 0, 0.06)',
        textAlign: 'center',
      } satisfies CSSProperties,
      heading: {
        fontSize: '2rem',
        fontWeight: '700',
        color: isDark ? '#f9fafb' : '#1a1a1a',
        margin: '0 0 1rem 0',
        lineHeight: '1.2',
      } satisfies CSSProperties,
      description: {
        fontSize: '1rem',
        color: isDark ? '#d1d5db' : '#6b7280',
        margin: '0 0 1.5rem 0',
        lineHeight: '1.5',
      } satisfies CSSProperties,
      countdown: {
        fontWeight: 'bold',
        fontSize: '1.1rem',
      } satisfies CSSProperties,
      errorBox: {
        padding: '1rem',
        backgroundColor: isDark ? '#7f1d1d' : '#fef2f2',
        borderRadius: '8px',
        border: isDark ? '1px solid #991b1b' : '1px solid #fecaca',
        marginTop: '1.5rem',
      } satisfies CSSProperties,
      errorText: {
        fontSize: '0.875rem',
        color: isDark ? '#f9fafb' : '#991b1b',
        margin: '0',
        fontFamily: 'monospace',
        wordBreak: 'break-word',
        textAlign: 'left',
        whiteSpace: 'pre-wrap',
      } satisfies CSSProperties,
    };
  }, [theme]);

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h1 style={styles.heading}>Unexpected application error</h1>
        <p style={styles.description}>
          Redirecting to home page in{' '}
          <strong style={styles.countdown}>{secondsLeft}</strong> seconds...
        </p>
        <div style={styles.errorBox}>
          <p style={styles.errorText}>{String(error)}</p>
        </div>
      </div>
    </div>
  );
};

const useCountdown = (initialSeconds: number) => {
  const [secondsLeft, setSecondsLeft] = useState(initialSeconds);

  useEffect(() => {
    const interval = setInterval(() => {
      setSecondsLeft((secondsLeft) => Math.max(secondsLeft - 1, 0));
    }, 1_000);

    return () => {
      clearInterval(interval);
    };
  }, []);

  return secondsLeft;
};
